import { useCallback, useEffect, useRef, useState } from "react"

/** Ab dieser Fensterbreite lässt sich verschieben. Darunter füllt ein Dialog
 *  ohnehin den Bildschirm — dort wäre Ziehen nur eine Falle. */
const AB_BREITE = 900
/** Wie viel vom Dialog immer sichtbar bleiben muss. Ohne das schiebt man ihn
 *  aus dem Fenster und kommt nicht mehr heran. */
const RAND = 60

/**
 * Macht einen Dialog verschiebbar (#47).
 *
 * Marlene am 26.08.2026: „es wäre gut, wenn sich unsere Popup fenster
 * verschieben liessen, ich würde oft gerne in der Mail drunter boch was lesen".
 * Der Dialog steht mittig über der Mail — und verdeckt genau den Text, auf den
 * er sich bezieht.
 *
 * 🔑 An EINER Stelle für alle Dialoge. Sie hängen ohnehin alle per
 * `createPortal` an `document.body` (Pflicht wegen `overflow`, siehe
 * `CreateContactModal.tsx`), also gibt es hier einen gemeinsamen Ort. Drag-Code
 * je Dialog wäre der nächste Fall von „eine Regel, an n Stellen dupliziert"
 * (vgl. #41).
 *
 * Rückgabe:
 *   `griff`  — auf die Kopfzeile legen (`{...griff}`)
 *   `stil`   — auf den Dialog-Kasten legen (`style={{...eigenes, ...stil}}`)
 *
 * `begrenzen` ersetzt die Grenze für Kästen, die nicht mittig stehen — etwa
 * den Versandhinweis oben rechts (#17). Ohne Angabe: mittiger Dialog.
 */
type Versatz = { x: number; y: number }

export function useVerschiebbar(offen: boolean, begrenzen?: (v: Versatz) => Versatz) {
  const [versatz, setVersatz] = useState<Versatz | null>(null)
  const grenze = useRef(begrenzen)
  grenze.current = begrenzen
  const zieht = useRef<{ startX: number; startY: number; vonX: number; vonY: number } | null>(null)

  // Beim Öffnen zurück in die Mitte. Eine gemerkte Position ist auf einem
  // kleineren Bildschirm im Weg — und niemand sucht einen Dialog, den er
  // gestern an den Rand geschoben hat.
  useEffect(() => { if (offen) setVersatz(null) }, [offen])

  // 🔴 Die Listener haengen an `offen`, NICHT an `zieht.current`. Ein Ref
  // loest kein Re-Render aus — mit `if (!zieht.current) return` lief dieser
  // Effekt nach dem Anfassen nie erneut, und der Dialog bewegte sich keinen
  // Pixel. Gefunden vom Waechter, nicht von Hand.
  useEffect(() => {
    if (!offen) return
    const bewegen = (e: PointerEvent) => {
      const z = zieht.current
      if (!z) return  // nicht am Ziehen — der Listener laeuft trotzdem mit
      const roh = { x: z.vonX + (e.clientX - z.startX), y: z.vonY + (e.clientY - z.startY) }
      if (grenze.current) { setVersatz(grenze.current(roh)); return }
      // Im Fenster halten: der halbe Bildschirm minus ein Rand ist die Grenze,
      // weil der Dialog von der Mitte aus versetzt wird.
      const maxX = Math.max(0, window.innerWidth / 2 - RAND)
      const maxY = Math.max(0, window.innerHeight / 2 - RAND)
      setVersatz({
        x: Math.max(-maxX, Math.min(maxX, roh.x)),
        y: Math.max(-maxY, Math.min(maxY, roh.y)),
      })
    }
    const loslassen = () => { zieht.current = null }
    window.addEventListener("pointermove", bewegen)
    window.addEventListener("pointerup", loslassen)
    return () => {
      window.removeEventListener("pointermove", bewegen)
      window.removeEventListener("pointerup", loslassen)
    }
  }, [offen])

  const anfassen = useCallback((e: React.PointerEvent) => {
    if (window.innerWidth < AB_BREITE) return
    // Nicht am Schliessen-Kreuz oder an einem Knopf in der Kopfzeile ziehen —
    // sonst schiebt man den Dialog, statt ihn zu schliessen.
    if ((e.target as HTMLElement).closest("button, a, input, select")) return
    zieht.current = {
      startX: e.clientX, startY: e.clientY,
      vonX: versatz?.x ?? 0, vonY: versatz?.y ?? 0,
    }
  }, [versatz])

  const verschiebbar = typeof window !== "undefined" && window.innerWidth >= AB_BREITE

  return {
    griff: verschiebbar
      ? {
          onPointerDown: anfassen,
          style: {
            cursor: "move",
            touchAction: "none" as const,
            // Ohne das markiert das Ziehen die Beschriftungen der Kopfzeile
            // blau — beim Durchklicken auf dev gesehen (26.08.2026).
            userSelect: "none" as const,
          },
        }
      : {},
    stil: versatz
      ? { transform: `translate(${versatz.x}px, ${versatz.y}px)` }
      : {},
    verschoben: !!versatz,
  }
}
